import React, { useState, useEffect } from 'react';
import { Modal }   from '../common/Modal';
import { Button }  from '../common/Button';
import { customFieldsAPI } from '../../services/api';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const inp = 'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

export function CustomFieldsManager({ isOpen, onClose, onFieldsChanged, appliesTo = 'all' }) {
  const [fields,  setFields]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding,  setAdding]  = useState(false);
  const [form,    setForm]    = useState({ label: '', type: 'text', position: 'header', appliesTo });

  const load = async () => {
    setLoading(true);
    try {
      const r = await customFieldsAPI.getAll({ appliesTo });
      setFields(r.data || []);
    } catch { toast.error('Failed to load custom fields'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (isOpen) load(); }, [isOpen]);

  const save = async () => {
    if (!form.label.trim()) return toast.error('Field label is required');
    try {
      await customFieldsAPI.create(form);
      toast.success('Custom field added');
      setForm({ label: '', type: 'text', position: 'header', appliesTo });
      setAdding(false);
      load();
      onFieldsChanged?.();
    } catch { toast.error('Failed to add field'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this custom field? It will no longer appear on new invoices.')) return;
    await customFieldsAPI.remove(id);
    load();
    onFieldsChanged?.();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage Custom Fields" size="lg">
      <div className="p-1 space-y-4">
        <p className="text-sm text-gray-500">
          Add custom fields like PO Number, Delivery Terms, or Reference Code — they'll appear
          on the invoice form and print on the PDF.
        </p>

        {/* Existing fields list */}
        <div className="border border-gray-200 rounded-xl divide-y divide-gray-100">
          {loading ? (
            <div className="p-4 text-center text-gray-400 text-sm">Loading...</div>
          ) : fields.length === 0 ? (
            <div className="p-4 text-center text-gray-400 text-sm">No custom fields yet</div>
          ) : fields.map(f => (
            <div key={f._id} className="flex items-center justify-between px-4 py-2.5">
              <div>
                <span className="text-sm font-medium text-gray-800">{f.label}</span>
                <span className="ml-2 text-xs text-gray-400">
                  {f.type} · {f.position === 'header' ? 'Top of invoice' : 'Bottom of invoice'}
                </span>
              </div>
              <button onClick={() => remove(f._id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg">
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {/* Add new field form */}
        {!adding ? (
          <Button variant="outline" onClick={() => setAdding(true)} className="w-full justify-center">
            <PlusIcon className="w-4 h-4 mr-1.5" /> Add Custom Field
          </Button>
        ) : (
          <div className="border border-indigo-200 bg-indigo-50/50 rounded-xl p-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Field Label
              </label>
              <input className={inp} value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                placeholder="e.g. PO Number, Delivery Terms" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Field Type</label>
                <select className={inp} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Position on PDF</label>
                <select className={inp} value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))}>
                  <option value="header">Top of invoice</option>
                  <option value="footer">Bottom of invoice</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
              <Button onClick={save}>Save Field</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}